import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { URLS } from '@wix/da-url';
import { useVerification } from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import { GallectionSourceType } from '../../../../../types/gallection';
import { ModuleType } from '../../../../../types/modules';
import EmptyState from '../../../EmptyState';
import { ExhibitionistBoxType } from '../ExhibitionistBox';

import s from './ExhibitionistEmptyState.scss';

const componentLocale = 'widgets.exhibitionist.empty';
const navigateTo = url =>
  typeof window !== 'undefined' && (window as any).IS_STORYBOOK
    ? alert(`navigate to ${url}`)
    : window.location.assign(url);

export interface Props {
  moduleId: number;
  exhibitionistType: ExhibitionistBoxType;
  view: 'large' | 'small' | 'wide';
  sourceType: GallectionSourceType;
  isOwner: boolean;
  profileOwnerUser: PapiUser;
  className?: string;
}

const ExhibitionistEmptyState: React.FC<Props> = ({
  exhibitionistType,
  view,
  sourceType,
  isOwner,
  profileOwnerUser,
  className,
}) => {
  const username = profileOwnerUser.username;
  const { t } = useTranslation();
  const { restrictToVerified } = useVerification();

  const isGroupProfile = profileOwnerUser && isGroup(profileOwnerUser);
  const viewer = isOwner ? 'owner' : 'visitor';
  const localePrefix = `${componentLocale}.${exhibitionistType}.${sourceType}`;
  const localeUsername = viewer === 'visitor' ? { username } : undefined;
  const title = t(
    isGroupProfile && isOwner
      ? `${componentLocale}.group.title`
      : `${localePrefix}.${viewer}.title`,
    localeUsername
  );
  const subtitle = t(
    isGroupProfile && isOwner
      ? `${componentLocale}.group.subtitle`
      : `${localePrefix}.${viewer}.subtitle`,
    localeUsername
  );

  const ownerCTALocale = `${localePrefix}.owner.CTALabel`;

  return (
    <EmptyState
      view={view}
      className={classnames(className, s[`${exhibitionistType}_${sourceType}`])}
      title={title}
      subtitle={subtitle}
      withButton={isOwner}
      {...(sourceType === GallectionSourceType.COLLECTION
        ? {
            buttonLabel: t(ownerCTALocale),
            onButtonClick: () => navigateTo(URLS.ROOT),
          }
        : {
            buttonLabel: t(
              isGroupProfile && isOwner
                ? `${componentLocale}.group.CTALabel`
                : ownerCTALocale
            ),
            onButtonClick: restrictToVerified('submit', () =>
              navigateTo(URLS.SUBMIT)
            ),
            buttonVariant: 'approval-green',
            biData: {
              evid: 704,
              sectionname: ModuleType.EXHIBITIONIST,
            },
          })}
    />
  );
};

export default ExhibitionistEmptyState;
